<div class="min-h-screen flex flex-col">
    <app-header [cartCount]="productService.getLocalCartProductLength()"></app-header>
    <div
        class="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-6 px-2 sm:px-4 lg:px-6 relative overflow-hidden">

        <!-- Decorative blurred circles -->
        <div
            class="absolute top-0 -left-20 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse">
        </div>
        <div
            class="absolute bottom-0 -right-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse delay-1000">
        </div>
        <div
            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-200 rounded-full filter blur-3xl opacity-20">
        </div>

        <div class="max-w-4xl mx-auto relative z-10">
            <!-- Main Card with glass effect - reduced padding -->
            <div
                class="backdrop-blur-xl bg-white/70 rounded-2xl shadow-2xl border border-white/50 overflow-hidden transition-all duration-500 hover:shadow-[0_20px_70px_-15px_rgba(0,0,0,0.2)]">

                <!-- Header with gradient overlay - more compact -->
                <div
                    class="relative px-6 pt-6 pb-4 md:px-8 bg-gradient-to-r from-indigo-600/90 via-purple-600/90 to-pink-600/90 backdrop-blur-sm">
                    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                        <div>
                            <h1 class="text-3xl md:text-4xl font-bold text-white tracking-tight drop-shadow-lg">Profile
                            </h1>
                            <p class="text-indigo-100 mt-1 text-xs md:text-sm font-light">Manage your personal
                                information
                            </p>
                        </div>
                        <!-- Avatar with ring - slightly smaller -->
                        <div class="relative group">
                            <div
                                class="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-300 to-pink-300 blur-md group-hover:blur-xl transition-all">
                            </div>
                            <div
                                class="relative h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg ring-4 ring-white/30">
                                <svg class="h-8 w-8 text-white drop-shadow" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <!-- Decorative wave -->
                    <div class="absolute bottom-0 left-0 right-0 h-4 bg-white/10 backdrop-blur-sm rounded-t-2xl"></div>
                </div>

                <!-- Form Section - reduced padding -->
                <div class="px-4 py-4 md:px-6 md:py-6">
                    <form [formGroup]="profileForm" (ngSubmit)="onProfileUpdateClick()">
                        <!-- Grid Layout - smaller gaps -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">

                            <!-- Name Field - Compact floating label -->
                            <div class="relative group">
                                <input type="text" formControlName="name" id="name"
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-800 bg-white/60 rounded-lg border-2 border-gray-200/80 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 peer transition-all duration-300 text-sm"
                                    placeholder=" " />
                                <label for="name"
                                    class="absolute text-xs text-gray-500 duration-300 transform -translate-y-2.5 scale-75 top-2 left-3 origin-[0] peer-focus:text-indigo-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2.5 peer-focus:scale-75 peer-focus:-translate-y-2.5">
                                    Full Name <span class="text-rose-500">*</span>
                                </label>
                                <div *ngIf="profileForm.get('name')?.invalid && profileForm.get('name')?.touched"
                                    class="text-rose-500 text-xs mt-0.5 flex items-center gap-1">
                                    <span>⚠️</span> Name is required
                                </div>
                                <div
                                    class="absolute right-3 top-2.5 text-gray-400 group-focus-within:text-indigo-500 transition-colors">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Date of Birth -->
                            <div class="relative group">
                                <input type="date" formControlName="dob" [max]="minDate" id="dob"
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-800 bg-white/60 rounded-lg border-2 border-gray-200/80 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 peer transition-all duration-300 text-sm" />
                                <label for="dob"
                                    class="absolute text-xs text-gray-500 duration-300 transform -translate-y-2.5 scale-75 top-2 left-3 origin-[0] peer-focus:text-indigo-600">
                                    Date of Birth <span class="text-rose-500">*</span>
                                </label>
                                <div class="absolute right-3 top-2.5 text-gray-400">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Mobile (Read-only) - compact -->
                            <div class="relative group opacity-90">
                                <input type="tel" formControlName="phoneNo" id="phone" readonly
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-500 bg-gray-100/80 rounded-lg border-2 border-gray-200/80 cursor-not-allowed text-sm" />
                                <label for="phone" class="absolute text-xs text-gray-500 top-2 left-3">Mobile <span
                                        class="text-rose-500">*</span></label>
                                <div class="absolute right-3 top-2.5 text-gray-400">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Email (Read-only) -->
                            <div class="relative group opacity-90">
                                <input type="email" formControlName="email" id="email" readonly
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-500 bg-gray-100/80 rounded-lg border-2 border-gray-200/80 cursor-not-allowed text-sm" />
                                <label for="email" class="absolute text-xs text-gray-500 top-2 left-3">Email <span
                                        class="text-rose-500">*</span></label>
                                <div class="absolute right-3 top-2.5 text-gray-400">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Spouse Name -->
                            <div class="relative group">
                                <input type="text" formControlName="spouseName" id="spouseName"
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-800 bg-white/60 rounded-lg border-2 border-gray-200/80 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 peer transition-all duration-300 text-sm"
                                    placeholder=" " />
                                <label for="spouseName"
                                    class="absolute text-xs text-gray-500 duration-300 transform -translate-y-2.5 scale-75 top-2 left-3 origin-[0] peer-focus:text-indigo-600">
                                    Spouse Name
                                </label>
                                <div class="absolute right-3 top-2.5 text-gray-400">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Spouse D.O.B -->
                            <div class="relative group">
                                <input type="date" formControlName="spouseDob" [max]="minDate" id="spouseDob"
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-800 bg-white/60 rounded-lg border-2 border-gray-200/80 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 peer transition-all duration-300 text-sm" />
                                <label for="spouseDob"
                                    class="absolute text-xs text-gray-500 duration-300 transform -translate-y-2.5 scale-75 top-2 left-3 origin-[0] peer-focus:text-indigo-600">
                                    Spouse D.O.B
                                </label>
                                <div class="absolute right-3 top-2.5 text-gray-400">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Anniversary Date - Full width, compact -->
                            <div class="md:col-span-2 relative group">
                                <input type="date" formControlName="anniversaryDate" id="anniversary"
                                    class="block w-full px-3 pt-5 pb-1.5 text-gray-800 bg-white/60 rounded-lg border-2 border-gray-200/80 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 peer transition-all duration-300 text-sm" />
                                <label for="anniversary"
                                    class="absolute text-xs text-gray-500 duration-300 transform -translate-y-2.5 scale-75 top-2 left-3 origin-[0] peer-focus:text-indigo-600">
                                    Anniversary Date
                                </label>
                                <div class="absolute right-3 top-2.5 text-gray-400">
                                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <p class="text-xs text-gray-400 mt-0.5 ml-1">* Celebrate your special day with us</p>
                            </div>
                        </div>

                        <!-- Action Buttons - compact -->
                        <div
                            class="mt-6 flex flex-col sm:flex-row gap-3 justify-end items-center border-t border-white/30">
                            <button type="submit"
                                class="group relative w-full sm:w-auto px-8 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 overflow-hidden text-sm">
                                <span class="relative z-10 flex items-center justify-center gap-2">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Update Profile
                                </span>
                                <div
                                    class="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                </div>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Footer note -->
            <p class="text-center text-gray-500 text-xs mt-6 backdrop-blur-sm">🔒 Your information is securely encrypted
            </p>
        </div>
    </div>
</div>